"use client"

import { useEffect, useRef, useState } from "react"
import { Check } from "lucide-react"

import { cn } from "@/common/lib/utils"

/**
 * Streaming log primitive for the simulated integration flows.
 *
 * Lines appear one by one on a caller-supplied schedule, each with a check mark
 * once "done". Purely presentational; the schedule is deterministic (delays come
 * from `data/connectors.ts`, hash-seeded) so two demo runs look identical.
 */

export interface SyncLogLine {
    id: string
    text: string
    /** Dimmed secondary text after the main label. */
    detail?: string
    /** Amber "review" marker instead of the green check. */
    flagged?: boolean
}

interface SyncLogProps {
    lines: SyncLogLine[]
    /** ms before line i appears (cumulative schedule is built by the caller). */
    delayFor: (index: number) => number
    onDone?: () => void
    className?: string
    /** Max height; the log auto-scrolls to the newest line. */
    maxHeight?: number
}

export default function SyncLog({ lines, delayFor, onDone, className, maxHeight = 240 }: SyncLogProps) {
    const [visible, setVisible] = useState(0)
    const containerRef = useRef<HTMLDivElement>(null)
    const onDoneRef = useRef(onDone)

    useEffect(() => {
        onDoneRef.current = onDone
    }, [onDone])

    // The schedule starts on mount. Callers render one SyncLog instance per
    // phase (different JSX branches), so a phase change remounts and the
    // counter starts at 0 without any reset-in-effect.
    useEffect(() => {
        let index = 0
        let cancelled = false
        let timer: ReturnType<typeof setTimeout>

        const step = () => {
            if (cancelled) return
            index += 1
            setVisible(index)
            if (index >= lines.length) {
                onDoneRef.current?.()
                return
            }
            timer = setTimeout(step, delayFor(index))
        }
        timer = setTimeout(step, delayFor(0))

        return () => {
            cancelled = true
            clearTimeout(timer)
        }
        // Restart only when the line set itself changes, not on parent re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines])

    useEffect(() => {
        containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight })
    }, [visible])

    return (
        <div
            ref={containerRef}
            className={cn(
                "overflow-y-auto rounded-md border bg-muted/30 p-3 font-mono text-xs leading-relaxed",
                className,
            )}
            style={{ maxHeight }}
        >
            {lines.slice(0, visible).map((line) => (
                <div key={line.id} className="flex items-start gap-2 py-0.5">
                    {line.flagged ? (
                        <span className="mt-px shrink-0 text-amber-600 dark:text-amber-400">!</span>
                    ) : (
                        <Check className="mt-px size-3 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    )}
                    <span className="min-w-0">
                        {line.text}
                        {line.detail && (
                            <span className="ml-2 text-muted-foreground">{line.detail}</span>
                        )}
                    </span>
                </div>
            ))}
            {visible < lines.length && (
                <div className="flex items-center gap-2 py-0.5 text-muted-foreground">
                    <span className="inline-block size-3 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    …
                </div>
            )}
        </div>
    )
}
